import React from 'react';
import { faqData } from '../data/mock';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { HelpCircle, MessageCircle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const FAQPage = ({ isDarkMode }) => {
  return (
    <div className={`min-h-screen pt-24 pb-20 ${isDarkMode ? 'bg-[#111A19]' : 'bg-[#E5D9B6]'}`}>
      {/* Background decorations */}
      <div className="fixed inset-0 pointer-events-none">
        <div className={`absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full blur-[120px] ${isDarkMode ? 'bg-[#284139]/20' : 'bg-[#284139]/10'}`} />
        <div className={`absolute top-1/4 right-0 w-[400px] h-[400px] rounded-full blur-[100px] ${isDarkMode ? 'bg-[#E5D9B6]/5' : 'bg-[#284139]/5'}`} />
      </div>

      <div className="max-w-4xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Back button */}
        <Link 
          to="/"
          className={`inline-flex items-center gap-2 mb-10 transition-colors group ${
            isDarkMode ? 'text-[#809076] hover:text-[#E5D9B6]' : 'text-[#284139]/70 hover:text-[#284139]'
          }`}
        >
          <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
          Back to Home
        </Link>

        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="relative inline-block mb-6">
            <div className={`w-20 h-20 rounded-3xl flex items-center justify-center animate-pulse-glow ${
              isDarkMode ? 'bg-gradient-to-br from-[#E5D9B6]/20 to-[#284139]' : 'bg-gradient-to-br from-[#284139]/20 to-[#284139]'
            }`}>
              <HelpCircle className={`w-10 h-10 ${isDarkMode ? 'text-[#E5D9B6]' : 'text-[#E5D9B6]'}`} />
            </div>
          </div>
          <span className={`block text-sm font-semibold tracking-[0.2em] uppercase mb-4 ${isDarkMode ? 'text-[#E5D9B6]' : 'text-[#284139]'}`}>
            FAQ
          </span>
          <h1 className={`font-display text-4xl lg:text-6xl font-bold mb-5 ${isDarkMode ? 'text-[#f5f5f5]' : 'text-[#284139]'}`}>
            Frequently Asked Questions
          </h1>
          <p className={`text-lg max-w-xl mx-auto ${isDarkMode ? 'text-[#809076]' : 'text-[#284139]/70'}`}>
            Find answers to common questions about our programs, eligibility, and application process.
          </p>
          <div className={`w-24 h-1.5 rounded-full mx-auto mt-8 ${isDarkMode ? 'bg-[#E5D9B6]' : 'bg-[#284139]'}`} />
        </div>

        {/* Accordion with premium styling */}
        <Accordion type="single" collapsible className="space-y-4">
          {faqData.map((item, index) => (
            <AccordionItem
              key={index}
              value={`item-${index}`}
              className={`border rounded-2xl px-7 transition-all duration-300 overflow-hidden ${
                isDarkMode 
                  ? 'border-[#284139] bg-[#1a2625]/50 data-[state=open]:border-[#E5D9B6]/40 data-[state=open]:bg-[#1a2625]'
                  : 'border-[#284139]/30 bg-[#284139] data-[state=open]:border-[#E5D9B6]/40 data-[state=open]:bg-[#284139]'
              }`}
            >
              <AccordionTrigger className={`text-left font-medium py-6 text-base [&[data-state=open]>svg]:rotate-180 [&>svg]:transition-transform [&>svg]:duration-300 ${
                isDarkMode 
                  ? 'text-[#f5f5f5] hover:text-[#E5D9B6] [&>svg]:text-[#E5D9B6]'
                  : 'text-[#E5D9B6] hover:text-[#E5D9B6] [&>svg]:text-[#E5D9B6]'
              }`}>
                <span className="pr-6">{item.question}</span>
              </AccordionTrigger>
              <AccordionContent className={`pb-6 leading-relaxed text-base ${isDarkMode ? 'text-[#809076]' : 'text-[#E5D9B6]/80'}`}>
                {item.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Contact CTA with premium card */}
        <div className={`mt-14 text-center p-10 rounded-3xl border relative overflow-hidden ${
          isDarkMode 
            ? 'bg-gradient-to-br from-[#1a2625] to-[#111A19] border-[#284139]'
            : 'bg-gradient-to-br from-[#284139] to-[#1a2625] border-[#284139]'
        }`}>
          {/* Decorative gradient */}
          <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-bl from-[#E5D9B6]/10 to-transparent rounded-bl-full" />
          
          <div className="relative z-10">
            <MessageCircle className="w-10 h-10 text-[#E5D9B6] mx-auto mb-4" />
            <p className={`mb-3 text-lg ${isDarkMode ? 'text-[#809076]' : 'text-[#E5D9B6]/80'}`}>
              Didn't find what you're looking for?
            </p>
            <p className={`text-base ${isDarkMode ? 'text-[#809076]' : 'text-[#E5D9B6]/80'}`}>
              Email us at{' '}
              <a
                href="mailto:consulting@pathwaysafrica.info"
                className="text-[#E5D9B6] hover:underline font-semibold transition-colors duration-200"
              >
                consulting@pathwaysafrica.info
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
